import React from 'react';
import { Check, Zap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import './SubscriptionPlans.css';

const SubscriptionPlans = ({ onSelectPlan, currentPlan = 'free', showAsModal = false }) => {
  const { language } = useLanguage();
  const t = translations[language];

  const plans = [
    {
      id: 'free',
      name: t.pricing?.freePlan || 'Free Plan',
      price: t.pricing?.free || '€0',
      priceAmount: 0,
      period: '',
      color: 'green',
      icon: '🌱',
      popular: false,
      tagline: t.pricing?.freeTagline || 'Try AnimLearn with basic features',
      credits: {
        initial: t.pricing?.freeInitialCredits || '4 Credits (first 7 days)',
        ongoing: t.pricing?.freeOngoingCredits || '2 Credits per day',
        note: t.pricing?.freeCreditsNote || '2 videos of 1 min per day for first week, then 1 video per day',
      },
      features: [
        t.pricing?.free720p || '720p resolution',
        t.pricing?.freeVoice || 'One default voice',
        t.pricing?.freeLength || 'Up to 30 seconds per video (1 credit)',
        t.pricing?.freeWatermark || 'Watermark included',
        t.pricing?.freeQueue || 'Slower rendering queue',
        t.pricing?.freeLinks || 'Public video links',
      ],
      bestFor: [
        t.pricing?.freeBest1 || 'First-time users',
        t.pricing?.freeBest2 || 'Students testing concepts',
        t.pricing?.freeBest3 || 'Quick previews',
      ],
    },
    {
      id: 'hobby',
      name: t.pricing?.hobbyPlan || 'Hobby Plan',
      price: '€15',
      priceAmount: 15,
      period: t.pricing?.perMonth || '/ month',
      color: 'blue',
      icon: '🔵',
      popular: true,
      tagline: t.pricing?.hobbyTagline || 'Perfect for individuals & educators',
      credits: {
        monthly: t.pricing?.hobbyMonthlyCredits || '200 credits / month',
        note: t.pricing?.creditsReset || 'Credits reset monthly',
      },
      stripeLink: '', // Will be filled with Stripe payment link
      features: [
        t.pricing?.hobbyDaily || 'Up to 10 videos per day',
        t.pricing?.hobby1080p || '1080p (Full HD) resolution',
        t.pricing?.hobbyVoice || 'Choose a voice from the voice library',
        t.pricing?.hobbyLength || 'Up to 2 minutes per video (max 4 credits)',
        t.pricing?.hobbyRendering || 'Faster rendering',
        t.pricing?.hobbyNoWatermark || 'No watermark',
        t.pricing?.hobbyPrivate || 'Private video storage',
      ],
      bestFor: [
        t.pricing?.hobbyBest1 || 'Teachers',
        t.pricing?.hobbyBest2 || 'YouTube creators',
        t.pricing?.hobbyBest3 || 'Regular content creators',
      ],
    },
    {
      id: 'pro',
      name: t.pricing?.proPlan || 'Pro Plan',
      price: '€39',
      priceAmount: 39,
      period: t.pricing?.perMonth || '/ month',
      color: 'red',
      icon: '🔴',
      popular: false,
      tagline: t.pricing?.proTagline || 'Built for power users & institutions',
      credits: {
        monthly: t.pricing?.proMonthlyCredits || '800 credits / month',
        note: t.pricing?.creditsReset || 'Credits reset monthly',
      },
      stripeLink: '', // Will be filled with Stripe payment link
      features: [
        t.pricing?.proDaily || 'Up to 50 videos per day',
        t.pricing?.pro1080p || '1080p (Full HD) resolution',
        t.pricing?.proVoiceCloning || 'Voice cloning',
        t.pricing?.proVoiceList || 'Choose a voice from the voice library',
        t.pricing?.proLength || 'Up to 5 minutes per video (max 10 credits)',
        t.pricing?.proPriority || 'Priority rendering',
        t.pricing?.proBatch || 'Batch video generation',
        t.pricing?.proAPI || 'API access',
        t.pricing?.proBranding || 'Custom branding',
      ],
      bestFor: [
        t.pricing?.proBest1 || 'Course creators',
        t.pricing?.proBest2 || 'EdTech companies',
        t.pricing?.proBest3 || 'Teams & institutions',
      ],
    },
    {
      id: 'credits',
      name: t.pricing?.creditsPlan || 'Buy Credits',
      price: t.pricing?.oneTimePurchase || 'One-time',
      priceAmount: 0,
      period: '',
      color: 'purple',
      icon: '💳',
      popular: false,
      tagline: t.pricing?.creditsTagline || 'No subscription required',
      creditInfo: {
        explanation: t.pricing?.creditExplanation || '1 credit = up to 30 seconds of video',
        roundingNote: t.pricing?.roundingNote || 'Credits are rounded up based on video length',
      },
      creditPacks: [
        { credits: 10, price: '€3', priceAmount: 3, stripeLink: '' },
        { credits: 50, price: '€15', priceAmount: 15, stripeLink: '' },
        { credits: 200, price: '€45', priceAmount: 45, stripeLink: '' },
      ],
      features: [
        t.pricing?.creditsNeverExpire || 'Credits never expire',
        t.pricing?.creditsAnyPlan || 'Can be used on any plan',
        t.pricing?.creditsOccasional || 'Ideal for occasional usage',
      ],
      bestFor: [],
    },
  ];

  const handleSelectPlan = (plan, creditPack = null) => {
    if (plan.id === 'free') {
      onSelectPlan?.(plan);
    } else if (plan.id === 'credits' && creditPack) {
      if (creditPack.stripeLink) {
        window.location.href = creditPack.stripeLink;
      } else {
        onSelectPlan?.({ ...plan, selectedPack: creditPack });
      }
    } else if (plan.stripeLink) {
      window.location.href = plan.stripeLink;
    } else {
      onSelectPlan?.(plan);
    }
  };

  return (
    <div className={`subscription-plans ${showAsModal ? 'modal-view' : ''}`}>
      {!showAsModal && (
        <div className="plans-header">
          <h2>{t.pricing?.chooseYourPlan || 'Choose Your Plan'}</h2>
          <p>{t.pricing?.planSubtitle || 'Select the perfect plan for your learning journey'}</p>
          <div className="credit-info-banner">
            <Zap size={18} />
            <span>{t.pricing?.creditSystem || '1 credit = up to 30 seconds of video'}</span>
          </div>
        </div>
      )}

      <div className="plans-grid">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`plan-card ${plan.popular ? 'popular' : ''} ${
              currentPlan === plan.id ? 'current' : ''
            } plan-${plan.color}`}
          >
            {plan.popular && (
              <div className="popular-badge">
                {t.pricing?.mostPopular || 'Most Popular'}
              </div>
            )}

            {currentPlan === plan.id && plan.id !== 'credits' && (
              <div className="current-badge">
                {t.pricing?.currentPlan || 'Current Plan'}
              </div>
            )}

            <div className="plan-header">
              <div className="plan-icon">{plan.icon}</div>
              <h3 className="plan-name">{plan.name}</h3>
              <p className="plan-tagline">{plan.tagline}</p>
              <div className="plan-price">
                <span className="price-amount">{plan.price}</span>
                {plan.period && <span className="price-period">{plan.period}</span>}
              </div>
            </div>

            {/* Credits Info */}
            {plan.credits && (
              <div className="credits-info">
                {plan.credits.monthly && (
                  <div className="credits-monthly">
                    <strong>{plan.credits.monthly}</strong>
                  </div>
                )}
                {plan.credits.initial && (
                  <div className="credits-initial">
                    <strong>{t.pricing?.firstWeek || "1st week after joining"}:</strong>
                    <span>{plan.credits.initial}</span>
                  </div>
                )}
                {plan.credits.ongoing && (
                  <div className="credits-ongoing">
                    <span>{plan.credits.ongoing}</span>
                  </div>
                )}
                {plan.credits.note && (
                  <div className="credits-note">{plan.credits.note}</div>
                )}
              </div>
            )}

            {/* Credit System Info for Credits Plan */}
            {plan.creditInfo && (
              <div className="credit-system-info">
                <p className="credit-explanation">
                  <Zap size={16} /> {plan.creditInfo.explanation}
                </p>
                <p className="credit-rounding">{plan.creditInfo.roundingNote}</p>
              </div>
            )}

            {/* Features */}
            <div className="plan-features">
              <h4>{t.pricing?.whatsIncluded || "What's included"}</h4>
              <ul>
                {plan.features.map((feature, index) => (
                  <li key={index}>
                    <Check size={18} className="check-icon" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Credit Packs */}
            {plan.creditPacks && (
              <div className="credit-packs">
                <h4>{t.pricing?.creditPacks || 'Credit packs'}</h4>
                {plan.creditPacks.map((pack, index) => (
                  <button
                    key={index}
                    className="credit-pack-btn"
                    onClick={() => handleSelectPlan(plan, pack)}
                  >
                    <span className="pack-credits">{pack.credits} {t.pricing?.credits || 'credits'}</span>
                    <span className="pack-price">{pack.price}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Best For */}
            {plan.bestFor && plan.bestFor.length > 0 && (
              <div className="best-for">
                <h4>{t.pricing?.bestFor || 'Best for'}</h4>
                <ul>
                  {plan.bestFor.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Button */}
            {plan.id !== 'credits' && (
              <div className="plan-footer">
                {plan.id === 'free' ? (
                  <button
                    className="btn-plan free-plan-btn"
                    disabled={currentPlan === 'free'}
                  >
                    {currentPlan === 'free'
                      ? t.pricing?.currentPlan || 'Current Plan'
                      : t.pricing?.alreadyFree || 'Always Free'}
                  </button>
                ) : (
                  <button
                    className={`btn-plan ${plan.popular ? 'btn-popular' : ''}`}
                    onClick={() => handleSelectPlan(plan)}
                    disabled={currentPlan === plan.id}
                  >
                    {currentPlan === plan.id
                      ? t.pricing?.currentPlan || 'Current Plan'
                      : plan.popular
                      ? t.pricing?.getStarted || 'Get Started'
                      : t.pricing?.choosePlan || 'Choose Plan'}
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Important Notes */}
      {!showAsModal && (
        <div className="plans-footer">
          <div className="important-notes">
            <h3>{t.pricing?.importantNotes || 'Important Notes'}</h3>
            <ul>
              <li>{t.pricing?.longerVideosNote || 'Longer videos use more credits.'}</li>
              <li>{t.pricing?.monthlyResetNote || 'Monthly subscription credits reset every billing cycle.'}</li>
              <li>{t.pricing?.oneTimeNote || 'One-time credits can be used anytime.'}</li>
            </ul>
          </div>
          <p className="plans-cancel">
            {t.pricing?.cancelAnytime || 'Cancel anytime. No hidden fees.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;
